/**
 * Carga perezosa del decodificador de códigos 2D (zxing-cpp compilado a WASM).
 *
 * Mismo patrón que `tesseractLoader`: script CDN pineado con SRI, y el módulo
 * memoizado. Cero bytes para quien no abre el modal del DNI.
 *
 * Por qué vale la pena bajarlo: el código de barras del DNI es la única fuente
 * con corrección de errores. Medido sobre una foto de un DNI sobre una mesa,
 * girado 90°, el OCR no devolvió un solo dato y el código entregó apellido,
 * nombres, sexo, documento, ejemplar y fechas, además de la orientación.
 *
 * Follow-up, igual que con tesseract: si se quiere eliminar el CDN, copiar
 * dist/iife/reader/ y el .wasm a public/ y apuntar acá.
 */

const ZXING_VERSION = "3.1.4";
const ZXING_SRC = `https://cdn.jsdelivr.net/npm/zxing-wasm@${ZXING_VERSION}/dist/iife/reader/index.js`;
const ZXING_SRI = "sha384-PN2+TcV6o3ygq0xnuFCxKBra0Yt4uPsZhoQ/vGYAwE57BrG9nHTvynv6YPyluWaU";
const ZXING_WASM = `https://cdn.jsdelivr.net/npm/zxing-wasm@${ZXING_VERSION}/dist/reader/zxing_reader.wasm`;

export interface PuntoDetectado {
  x: number;
  y: number;
}

export interface CodigoDetectado {
  texto: string;
  formato: string;
  /** Esquinas en orden superior-izquierda, superior-derecha, inferior-derecha, inferior-izquierda. */
  esquinas: [PuntoDetectado, PuntoDetectado, PuntoDetectado, PuntoDetectado] | null;
}

export interface DecodificadorDeCodigos {
  leer(imagen: Blob): Promise<CodigoDetectado[]>;
}

declare global {
  interface Window {
    ZXingWASM?: {
      readBarcodes: (imagen: Blob, opciones?: object) => Promise<any[]>;
      prepareZXingModule: (config: object) => unknown;
    };
  }
}

let scriptPromise: Promise<void> | null = null;
let decodificador: Promise<DecodificadorDeCodigos> | null = null;

function cargarScript(): Promise<void> {
  if (window.ZXingWASM) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = ZXING_SRC;
      s.integrity = ZXING_SRI;
      s.crossOrigin = "anonymous";
      s.onload = () => resolve();
      s.onerror = () => {
        scriptPromise = null;
        reject(new Error("codigo_load_failed"));
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

export function cargarDecodificador(): Promise<DecodificadorDeCodigos> {
  if (!decodificador) {
    decodificador = (async () => {
      await cargarScript();
      const zx = window.ZXingWASM!;
      // El .wasm se baja del mismo CDN. En el navegador alcanza con decirle
      // dónde está: a diferencia de node, acá sí puede ir a buscarlo.
      zx.prepareZXingModule({
        overrides: { locateFile: (p: string) => (p.endsWith(".wasm") ? ZXING_WASM : p) },
        fireImmediately: false,
      });
      return {
        async leer(imagen: Blob): Promise<CodigoDetectado[]> {
          const crudos = await zx.readBarcodes(imagen, {
            tryHarder: true,
            formats: ["PDF417", "QRCode"],
            maxNumberOfSymbols: 2,
          });
          return crudos.map((r) => ({
            texto: String(r.text ?? ""),
            formato: String(r.format ?? ""),
            esquinas: r.position
              ? [r.position.topLeft, r.position.topRight, r.position.bottomRight, r.position.bottomLeft]
              : null,
          }));
        },
      };
    })();
    decodificador.catch(() => {
      decodificador = null;
    });
  }
  return decodificador;
}
